/* REM — storefront behaviour
   - Drawers (menu + cart)
   - AJAX add to cart → opens cart drawer (Shopify Cart API + Section Rendering API)
   - Cart quantity / remove
   - Product gallery, thumbnails, quantity selector, variant picker
   - Home product card image dots
   Everything degrades to normal Shopify form posts / links if JavaScript fails. */
(() => {
  'use strict';

  const R = window.REM || { routes: { root: '/', cartAdd: '/cart/add', cartChange: '/cart/change', cart: '/cart' }, strings: {} };
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  /* ---------------- Drawers ---------------- */
  const overlay = () => $('[data-overlay]');
  let activeDrawer = null;
  let lastFocus = null;

  function openDrawer(name) {
    const drawer = $(`[data-drawer="${name}"]`);
    if (!drawer) return false;
    if (activeDrawer && activeDrawer !== drawer) closeDrawer(true);
    lastFocus = document.activeElement;
    const ov = overlay();
    ov.hidden = false;
    requestAnimationFrame(() => { ov.classList.add('is-visible'); drawer.classList.add('is-open'); });
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('is-locked');
    $$(`[data-drawer-open="${name}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    activeDrawer = drawer;
    setTimeout(() => drawer.focus({ preventScroll: true }), 50);
    return true;
  }

  function closeDrawer(keepOverlay) {
    if (!activeDrawer) return;
    const drawer = activeDrawer;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    $$(`[data-drawer-open="${drawer.dataset.drawer}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    activeDrawer = null;
    if (!keepOverlay) {
      const ov = overlay();
      ov.classList.remove('is-visible');
      setTimeout(() => { if (!activeDrawer) ov.hidden = true; }, 350);
      document.documentElement.classList.remove('is-locked');
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    }
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      // The cart page already shows the cart, so let the link work normally there.
      if (opener.dataset.drawerOpen === 'cart' && document.body.classList.contains('template-cart')) return;
      if (openDrawer(opener.dataset.drawerOpen)) e.preventDefault();
      return;
    }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer && activeDrawer) {
      // "Continue shopping" inside the drawer just closes it.
      e.preventDefault();
      closeDrawer();
      return;
    }
    if (e.target.matches('[data-overlay]')) closeDrawer();
  });

  document.addEventListener('keydown', (e) => {
    if (!activeDrawer) return;
    if (e.key === 'Escape') { closeDrawer(); return; }
    if (e.key === 'Tab') {
      const focusables = $$('a[href], button:not([disabled]), input:not([type="hidden"]), [tabindex]:not([tabindex="-1"])', activeDrawer)
        .filter((el) => el.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  /* ---------------- Back arrow ---------------- */
  document.addEventListener('click', (e) => {
    const back = e.target.closest('[data-back]');
    if (!back) return;
    let sameSite = false;
    try { sameSite = document.referrer && new URL(document.referrer).host === location.host; } catch (_) {}
    if (sameSite && history.length > 1) { e.preventDefault(); history.back(); }
  });

  /* ---------------- Cart rendering ---------------- */
  function renderCart(sections) {
    const html = sections && sections['cart-drawer'];
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = $('[data-cart-contents]', doc);
    if (!fresh) return;

    const drawerBody = $('[data-cart-drawer-body]');
    if (drawerBody) drawerBody.innerHTML = fresh.outerHTML;

    const cartPage = $('[data-cart-page]');
    if (cartPage) cartPage.innerHTML = fresh.outerHTML;

    updateCount(parseInt(fresh.dataset.count, 10) || 0);
  }

  function updateCount(count) {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  const sectionsParams = () => ({ sections: 'cart-drawer', sections_url: location.pathname });

  /* ---------------- Add to cart ---------------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form || !window.fetch) return;
    e.preventDefault();

    const button = $('[data-add-button]', form);
    const error = $('[data-form-error]', form);
    if (button && button.disabled) return;
    if (error) error.hidden = true;
    if (button) { button.classList.add('is-loading'); button.setAttribute('aria-busy', 'true'); }

    const body = new FormData(form);
    const p = sectionsParams();
    body.append('sections', p.sections);
    body.append('sections_url', p.sections_url);

    try {
      const res = await fetch(`${R.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body
      });
      const data = await res.json();
      if (!res.ok || data.status) {
        throw new Error(data.description || data.message || R.strings.error);
      }
      renderCart(data.sections);
      openDrawer('cart');
      if (button) {
        button.classList.add('is-added');
        setTimeout(() => button.classList.remove('is-added'), 600);
      }
    } catch (err) {
      if (error) { error.textContent = err.message || R.strings.error; error.hidden = false; }
    } finally {
      if (button) { button.classList.remove('is-loading'); button.removeAttribute('aria-busy'); }
    }
  });

  /* ---------------- Buy now (home card) ----------------
     Normal Shopify form post to /cart/add with return_to=/checkout, so it always lands on Shopify checkout. */
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-buy-now]');
    if (!form) return;
    const button = $('[data-buy-now-button]', form);
    if (button) { button.classList.add('is-loading'); button.setAttribute('aria-busy', 'true'); }
  });
  window.addEventListener('pageshow', () => {
    $$('[data-buy-now-button]').forEach((b) => { b.classList.remove('is-loading'); b.removeAttribute('aria-busy'); });
  });

  /* ---------------- Change quantity / remove ---------------- */
  document.addEventListener('click', async (e) => {
    const link = e.target.closest('[data-cart-change]');
    if (!link || !window.fetch) return;
    e.preventDefault();

    const wrap = link.closest('[data-cart-contents]');
    if (wrap) wrap.classList.add('is-updating');

    try {
      const res = await fetch(`${R.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: link.dataset.cartChange,
          quantity: Math.max(0, parseInt(link.dataset.quantity, 10) || 0),
          ...sectionsParams()
        })
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || R.strings.error);
      renderCart(data.sections);
    } catch (err) {
      if (wrap) wrap.classList.remove('is-updating');
      window.location.href = link.href; // fall back to Shopify's own cart change
    }
  });

  /* ---------------- Quantity selector ---------------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-minus], [data-qty-plus]');
    if (!btn) return;
    const input = $('input', btn.closest('[data-qty]'));
    const min = parseInt(input.min, 10) || 1;
    const max = parseInt(input.max, 10) || 99;
    const current = parseInt(input.value, 10) || min;
    const next = btn.hasAttribute('data-qty-plus') ? current + 1 : current - 1;
    input.value = Math.min(max, Math.max(min, next));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
  document.addEventListener('change', (e) => {
    if (!e.target.matches('.qty__input')) return;
    const input = e.target;
    const v = parseInt(input.value, 10);
    input.value = Math.min(parseInt(input.max, 10) || 99, Math.max(parseInt(input.min, 10) || 1, isNaN(v) ? 1 : v));
  });

  /* ---------------- Scroll-snap sliders (gallery + card) ---------------- */
  function watchSlider(track, onChange) {
    let ticking = false;
    track.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
        onChange(i);
      });
    }, { passive: true });
  }
  function goTo(track, index, smooth = true) {
    track.scrollTo({ left: index * track.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
  }

  function initGallery(root) {
    const track = $('[data-gallery-track]', root);
    if (!track) return null;
    const thumbs = $$('[data-thumb]', root);
    const setActive = (i) => {
      thumbs.forEach((t, n) => {
        const on = n === i;
        t.classList.toggle('is-active', on);
        if (on) { t.setAttribute('aria-current', 'true'); } else { t.removeAttribute('aria-current'); }
      });
      const active = thumbs[i];
      if (active) {
        const bar = active.parentElement;
        const left = active.offsetLeft - bar.clientWidth / 2 + active.clientWidth / 2;
        bar.scrollTo({ left, behavior: 'smooth' });
      }
    };
    thumbs.forEach((t) => t.addEventListener('click', () => {
      const i = parseInt(t.dataset.thumb, 10);
      goTo(track, i);
      setActive(i);
    }));
    watchSlider(track, setActive);

    // Start on the variant's image if the URL selected one.
    const initial = thumbs.findIndex((t) => t.classList.contains('is-active'));
    if (initial > 0) requestAnimationFrame(() => goTo(track, initial, false));

    return {
      showMedia(mediaId) {
        const slide = $(`[data-media-id="${mediaId}"]`, track);
        if (!slide) return;
        const i = parseInt(slide.dataset.index, 10);
        goTo(track, i);
        setActive(i);
      }
    };
  }

  function initCard(card) {
    const track = $('[data-card-track]', card);
    const dots = $$('[data-dot]', card);
    if (!track || !dots.length) return;
    watchSlider(track, (i) => dots.forEach((d, n) => d.classList.toggle('is-active', n === i)));
    dots.forEach((d) => d.addEventListener('click', () => goTo(track, parseInt(d.dataset.dot, 10))));
  }

  /* ---------------- Variant picker ---------------- */
  function initProduct(root) {
    const gallery = initGallery(root);
    const picker = $('[data-variant-picker]', root);
    const jsonEl = $('[data-product-json]', root);
    if (!picker || !jsonEl) return;

    let variants = [];
    try { variants = JSON.parse(jsonEl.textContent); } catch (_) { return; }

    const idInput = $('[data-variant-id]', root);
    const priceEl = $('[data-price]', root);
    const button = $('[data-add-button]', root);
    const label = $('[data-add-label]', root);

    picker.addEventListener('change', () => {
      const selected = $$('fieldset', picker).map((fs) => {
        const checked = $('input:checked', fs);
        return checked ? checked.value : null;
      });
      const variant = variants.find((v) => v.options.every((opt, i) => opt === selected[i]));

      if (!variant) {
        if (button) button.disabled = true;
        if (label) label.textContent = R.strings.unavailable;
        return;
      }
      if (idInput) idInput.value = variant.id;
      if (priceEl) priceEl.innerHTML = variant.price;
      if (button) button.disabled = !variant.available;
      if (label) label.textContent = variant.available ? R.strings.addToCart : R.strings.soldOut;
      if (variant.media && gallery) gallery.showMedia(variant.media);

      const url = new URL(location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url.toString());
    });
  }

  /* ---------------- Certified Lens Study pop-up ---------------- */
  const ZOOMS = [1, 1.5, 2, 3];
  function setZoom(dialog, level, focus) {
    const body = $('[data-study-body]', dialog);
    const img = $('[data-study-img]', dialog);
    if (!body || !img) return;
    const i = Math.max(0, Math.min(ZOOMS.length - 1, level));
    const prev = parseFloat(img.style.getPropertyValue('--zoom')) || 1;
    const next = ZOOMS[i];
    // Keep the point being looked at (or the centre) in place while zooming.
    const fx = focus ? focus.x : body.clientWidth / 2;
    const fy = focus ? focus.y : body.clientHeight / 2;
    const px = (body.scrollLeft + fx) / prev;
    const py = (body.scrollTop + fy) / prev;
    img.style.setProperty('--zoom', next);
    dialog.dataset.zoom = i;
    dialog.classList.toggle('is-zoomed', i > 0);
    body.scrollLeft = px * next - fx;
    body.scrollTop = py * next - fy;
    const out = $('[data-study-zoom="-1"]', dialog);
    const inn = $('[data-study-zoom="1"]', dialog);
    if (out) out.disabled = i === 0;
    if (inn) inn.disabled = i === ZOOMS.length - 1;
  }

  function openStudy(dialog) {
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    document.documentElement.classList.add('is-locked');
    setZoom(dialog, 0);
    const body = $('[data-study-body]', dialog);
    if (body) { body.scrollTop = 0; body.scrollLeft = 0; }
  }
  function closeStudy(dialog) {
    if (typeof dialog.close === 'function') dialog.close();
    else { dialog.removeAttribute('open'); document.documentElement.classList.remove('is-locked'); }
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-study-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.studyOpen);
      if (dialog) openStudy(dialog);
      return;
    }
    const dialog = e.target.closest('[data-study]');
    if (!dialog) return;
    if (e.target === dialog || e.target.closest('[data-study-close]')) { closeStudy(dialog); return; }
    const zoomBtn = e.target.closest('[data-study-zoom]');
    if (zoomBtn) { setZoom(dialog, (parseInt(dialog.dataset.zoom, 10) || 0) + parseInt(zoomBtn.dataset.studyZoom, 10)); return; }
    if (e.target.matches('[data-study-img]')) {
      // Click / double-tap the report: zoom in to 2x at that spot, or back to fit.
      const body = $('[data-study-body]', dialog);
      const r = body.getBoundingClientRect();
      const current = parseInt(dialog.dataset.zoom, 10) || 0;
      setZoom(dialog, current > 0 ? 0 : 2, { x: e.clientX - r.left, y: e.clientY - r.top });
    }
  });
  // Native <dialog> fires "close" for the X button, Esc key and backdrop click alike.
  document.addEventListener('close', (e) => {
    if (e.target.matches && e.target.matches('[data-study]')) document.documentElement.classList.remove('is-locked');
  }, true);

  /* ---------------- Boot ---------------- */
  function init() {
    $$('[data-product]').forEach(initProduct);
    $$('[data-card]').forEach(initCard);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Theme editor: re-initialise sections when they're edited.
  document.addEventListener('shopify:section:load', (e) => {
    $$('[data-product]', e.target).forEach(initProduct);
    $$('[data-card]', e.target).forEach(initCard);
  });

  // Returning via the back button (bfcache) can show a stale cart count.
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted || !window.fetch) return;
    fetch(`${R.routes.cart}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((c) => updateCount(c.item_count))
      .catch(() => {});
  });
})();
